import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Building } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter 827 - Unified settlement-method picker for invoice and bill payments.
 *
 * Lets the user choose between:
 *   • Bank account (cash settlement) - existing behaviour
 *   • Balance Sheet GL (non-cash settlement) - new in Iter 827
 *
 * The two are mutually exclusive: selecting one disables the other and
 * clears the disabled selection so the validation can be a simple
 * "exactly one of {bank_account_id, settlement_gl_code} must be set".
 *
 * Props:
 *   value: {
 *     settlement_method: 'bank' | 'gl',
 *     bank_account_id?: string,
 *     settlement_gl_code?: string,
 *   }
 *   onChange: (next value) => void
 *   direction: 'receive' | 'pay'   // for filtering excluded GLs
 *   currency?: string              // forwarded to BankAccountSelector
 *   disabled?: boolean
 *
 * iter1433 · SUGGESTED_GLS is now NAME-BASED, not code-based. The
 * legacy hardcoded codes (2520 Director's Loan, 2530 Intercompany,
 * 2580 Shareholder Loan, 1180 Other Receivable, 2110 Other Payable,
 * 3100 Owner Capital, 3200 Owner Drawings) only exist in a subset of
 * COA templates — NG tenants have `1150/2070 Due from/to Directors`
 * and `2530 Shareholder Loans`, while US templates use 2520/2530.
 * The picker now regex-matches the tenant's actual CoA names to
 * surface real suggestions per tenant, avoiding both "empty
 * Suggested optgroup" and "accounting-wrong suggestion" bugs
 * (e.g. offering 2110 Accrued Salaries as an offset AP because
 * legacy code 2110 also happened to exist in the template).
 */
const SUGGESTION_MATCHERS = {
  receive: [
    { re: /director'?s?\s*loan|due\s+from\s+director/i, label: "Director's Loan" },
    { re: /intercompany|inter[- ]company/i, label: 'Intercompany Loan' },
    { re: /shareholder\s*loan/i, label: 'Shareholder Loan' },
    { re: /owner('|s)?\s*(capital|contribution|equity)/i, label: 'Owner Contribution' },
    { re: /other\s+receivable/i, label: 'Other Receivable' },
  ],
  pay: [
    { re: /director'?s?\s*loan|due\s+to\s+director/i, label: "Director's Loan" },
    { re: /intercompany|inter[- ]company/i, label: 'Intercompany Loan' },
    { re: /shareholder\s*loan/i, label: 'Shareholder Loan' },
    { re: /owner('|s)?\s*(drawing|withdrawal|distribution)/i, label: 'Owner Drawings' },
    { re: /other\s+payable/i, label: 'Other Payable' },
  ],
};

export const SettlementMethodPicker = ({
  value = { settlement_method: 'bank' },
  onChange,
  direction = 'receive',
  currency,
  disabled = false,
  testIdPrefix = 'settlement',
  initialBankAccounts = null,
}) => {
  const [glAccounts, setGlAccounts] = useState([]);
  const [loadingGls, setLoadingGls] = useState(false);
  const [glError, setGlError] = useState('');
  const [bankAccounts, setBankAccounts] = useState(
    Array.isArray(initialBankAccounts) && initialBankAccounts.length > 0 ? initialBankAccounts : []
  );
  const [loadingBanks, setLoadingBanks] = useState(false);
  const method = value?.settlement_method || 'bank';
  // iter1433 · Local memory for the last-selected GL / bank so
  // toggling between bank <-> non-cash doesn't wipe the user's
  // choice. Seeded from `value` on mount and updated inside
  // `setMethod` before we push the null to the parent.
  const lastGlRef = useRef(value?.settlement_gl_code || null);
  const lastBankRef = useRef(value?.bank_account_id || null);
  useEffect(() => {
    if (value?.settlement_gl_code) lastGlRef.current = value.settlement_gl_code;
    if (value?.bank_account_id) lastBankRef.current = value.bank_account_id;
  }, [value?.settlement_gl_code, value?.bank_account_id]);

  useEffect(() => {
    // Caller-provided accounts avoid a refetch race that briefly renders a
    // pre-selected bank value as blank while the list loads.
    if (Array.isArray(initialBankAccounts) && initialBankAccounts.length > 0) return;
    let cancel = false;
    (async () => {
      try {
        setLoadingBanks(true);
        const { data } = await axios.get(`${API}/bank-accounts`);
        if (cancel) return;
        const list = Array.isArray(data) ? data : (data?.bank_accounts || []);
        setBankAccounts(list);
      } catch {
        if (!cancel) setBankAccounts([]);
      } finally {
        if (!cancel) setLoadingBanks(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  // Optional currency filter - when a transaction currency is supplied,
  // narrow the bank list to matching-currency accounts so users don't
  // accidentally settle a USD invoice from an NGN bank.
  const filteredBanks = useMemo(() => {
    if (!currency) return bankAccounts;
    return bankAccounts.filter((b) =>
      !b.currency || b.currency === currency
    );
  }, [bankAccounts, currency]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoadingGls(true);
        setGlError('');
        const { data } = await axios.get(`${API}/accounting/chart-of-accounts`);
        if (cancel) return;
        const accounts = Array.isArray(data) ? data
          : (data?.accounts || data?.chart_of_accounts || []);
        const bsTypes = new Set(['asset', 'liability', 'equity']);
        // Exclude AR/AP (1100/2100) and bank-linked GLs
        const excluded = new Set(['1050', '1060', '1100', '1110', '2100']);
        const filtered = accounts.filter((a) => {
          const t = (a.type || a.account_type || '').toLowerCase();
          if (!bsTypes.has(t)) return false;
          if (excluded.has(a.code)) return false;
          if (a.linked_bank_account_id) return false;
          return true;
        });
        setGlAccounts(filtered);
      } catch (e) {
        if (!cancel) setGlError('Failed to load Balance Sheet GLs — refresh to retry.');
      } finally {
        if (!cancel) setLoadingGls(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const grouped = useMemo(() => {
    const matchers = SUGGESTION_MATCHERS[direction] || [];
    const suggested = [];
    const seen = new Set();
    // For each matcher (in priority order), pick the FIRST CoA row
    // whose name matches. This yields a per-tenant, per-country
    // suggestion list without hardcoding codes.
    for (const m of matchers) {
      const hit = glAccounts.find(
        (a) => !seen.has(a.code) && m.re.test(String(a.name || '')),
      );
      if (hit) { suggested.push(hit); seen.add(hit.code); }
    }
    const others = glAccounts.filter((a) => !seen.has(a.code));
    return { suggested, others };
  }, [glAccounts, direction]);

  const setMethod = (next) => {
    // iter1433 · Preserve the previously chosen GL / bank across
    // toggles so bouncing between the two options doesn't lose the
    // user's selection. Uses picker-local refs because the parent
    // has already nulled the "away" side of `value` (bank id when
    // GL is active and vice-versa) — so we can't restore from
    // props alone.
    if (next === 'bank') {
      // Remember the current GL before we switch away.
      if (value?.settlement_gl_code) {
        lastGlRef.current = value.settlement_gl_code;
      }
      onChange?.({
        settlement_method: 'bank',
        bank_account_id: value?.bank_account_id || lastBankRef.current || null,
        settlement_gl_code: null,
      });
    } else {
      // Remember the current bank before we switch away.
      if (value?.bank_account_id) {
        lastBankRef.current = value.bank_account_id;
      }
      onChange?.({
        settlement_method: 'gl',
        bank_account_id: null,
        settlement_gl_code: value?.settlement_gl_code || lastGlRef.current || null,
      });
    }
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-picker`}>
      <div>
        <Label className="text-xs text-muted-foreground mb-1.5 block">
          Settlement method
        </Label>
        <div className="grid grid-cols-2 gap-2">
          <button type="button"
                    disabled={disabled}
                    onClick={() => setMethod('bank')}
                    data-testid={`${testIdPrefix}-method-bank`}
                    className={`border rounded-lg px-3 py-2 text-xs font-medium text-left transition ${
                      method === 'bank' ? 'border-[#283F8B] bg-[#283F8B]/5 text-[#283F8B]'
                                          : 'border-border bg-background hover:bg-muted/30'
                    }`}>
            🏦 Bank account
            <div className="text-[10px] text-muted-foreground mt-0.5 font-normal">
              Cash settlement: money moves
            </div>
          </button>
          <button type="button"
                    disabled={disabled}
                    onClick={() => setMethod('gl')}
                    data-testid={`${testIdPrefix}-method-gl`}
                    className={`border rounded-lg px-3 py-2 text-xs font-medium text-left transition ${
                      method === 'gl' ? 'border-[#283F8B] bg-[#283F8B]/5 text-[#283F8B]'
                                        : 'border-border bg-background hover:bg-muted/30'
                    }`}>
            📒 Non-cash settlement
            <div className="text-[10px] text-muted-foreground mt-0.5 font-normal">
              Settle against a Balance Sheet GL
            </div>
          </button>
        </div>
      </div>

      {method === 'bank' && (
        <div data-testid={`${testIdPrefix}-bank-block`}>
          <Label className="text-xs">Bank account</Label>
          <Select
            value={value?.bank_account_id || ''}
            onValueChange={(id) =>
              onChange?.({ ...value, settlement_method: 'bank',
                            bank_account_id: id, settlement_gl_code: null })}
            disabled={disabled || loadingBanks}
          >
            <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-bank-select`}>
              <SelectValue placeholder={
                loadingBanks ? 'Loading…'
                  : (filteredBanks.length === 0
                      ? (currency ? `No ${currency} bank accounts` : 'No bank accounts')
                      : 'Select a bank account…')
              } />
            </SelectTrigger>
            <SelectContent>
              {filteredBanks.map((b) => (
                <SelectItem key={b.id} value={b.id}
                              data-testid={`${testIdPrefix}-bank-option-${b.id}`}>
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-muted-foreground" />
                    <span>{b.bank_name} {b.account_number ? `· ${String(b.account_number).slice(-4)}` : ''}</span>
                    {b.currency && (
                      <span className="text-[10px] text-muted-foreground ml-1">({b.currency})</span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground mt-1 italic">
            Non-cash settlement option is disabled while a bank account is selected.
          </p>
        </div>
      )}

      {method === 'gl' && (
        <div data-testid={`${testIdPrefix}-gl-block`}>
          <Label className="text-xs">Balance Sheet GL</Label>
          <select
            disabled={disabled || loadingGls}
            value={value?.settlement_gl_code || ''}
            onChange={(e) =>
              onChange?.({ ...value, settlement_method: 'gl',
                            settlement_gl_code: e.target.value,
                            bank_account_id: null })}
            data-testid={`${testIdPrefix}-gl-select`}
            className="w-full border rounded-md px-3 py-2 text-sm bg-background mt-1"
          >
            <option value="">
              {loadingGls ? 'Loading…' : 'Select a Balance Sheet GL…'}
            </option>
            {grouped.suggested.length > 0 && (
              <optgroup label="Suggested">
                {grouped.suggested.map((a) => (
                  <option key={a.code} value={a.code}
                            data-testid={`${testIdPrefix}-gl-option-${a.code}`}>
                    {a.code}: {a.name}
                  </option>
                ))}
              </optgroup>
            )}
            {grouped.others.length > 0 && (
              <optgroup label="All Balance Sheet GLs">
                {grouped.others.map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.code}: {a.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <p className="text-[10px] text-muted-foreground mt-1 italic">
            Bank account selector is disabled while a non-cash settlement GL is chosen.
            No cash will move: this posts a journal-only settlement.
          </p>
          {glError && (
            <p className="text-[10px] text-red-600 mt-1"
               data-testid={`${testIdPrefix}-gl-error`}>
              {glError}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default SettlementMethodPicker;
